import { createApi } from '@reduxjs/toolkit/query/react';
import { client } from '../../../api/NestClient';
import { nestBaseQuery, type NestBaseQueryError } from '../../../utils/nestBaseQuery';
import type { paths } from '../../../api/types/api';

/** Error type when the response is an error. Re-export for typed usage (e.g. error.cause). */
export type { NestBaseQueryError };

/** User management permission IDs (from API schema) */
export type UserManagementPermission = import('../../../api/types/api').components['schemas']['UserManagementPermission'];

/** Permission enum values for convenience */
export const UserManagementPermissionEnum = {
  AddUsers: 'usermanagement.add_users' as UserManagementPermission,
  RemoveUser: 'usermanagement.remove_user' as UserManagementPermission,
  RemoveAdminUser: 'usermanagement.remove_admin_user' as UserManagementPermission,
  WildEntry: 'usermanagement.wild_entry' as UserManagementPermission,
} as const;

export type LookupUserByEmailResponse =paths['/api/v1/user-management/users/lookup']['get']['responses']['200']['content']['application/json'];

/**
 * Response type for list invitations endpoint
 */
export type ListInvitationsResponse =
  paths['/api/v1/user-management/invitations']['get']['responses']['200']['content']['application/json'];

/** Single invitation item from list invitations response */
export type InvitationListItem = ListInvitationsResponse['data'][number];

/**
 * Request body type for create invitation endpoint.
 * Extended with userId (existing user) or firstName/lastName (new user).
 */
export type CreateInvitationRequest = paths['/api/v1/user-management/invitations']['post']['requestBody']['content']['application/json'];

/**
 * Response type for create invitation endpoint
 */
type CreateInvitationResponse =
  paths['/api/v1/user-management/invitations']['post']['responses']['201']['content']['application/json'];

/**
 * Response type for list company users endpoint
 */
export type ListCompanyUsersResponse =
  paths['/api/v1/user-management/companies/users']['get']['responses']['200']['content']['application/json'];

/** Single company user item from list company users response */
export type CompanyUserListItem = ListCompanyUsersResponse['data'][number];

export type DeleteCompanyUserResponse =
paths['/api/v1/user-management/companies/users/{userId}']['delete']['responses']['200']['content']['application/json'];

/**
 * Query params for list invitations endpoint (from API schema)
 */
export type ListInvitationsQueryParams = NonNullable<
  paths['/api/v1/user-management/invitations']['get']['parameters']['query']
>;

/** Response type for list join-requests endpoint */
export type ListJoinRequestsResponse =
  paths['/api/v1/user-management/join-requests']['get']['responses']['200']['content']['application/json'];

/** Single join request item from list join-requests response */
export type JoinRequestItem = ListJoinRequestsResponse['data'][number];

export interface ListJoinRequestsQueryParams {
  status?: string;
}

/**
 * Response type for list roles endpoint (proper type from API schema)
 */
type ListRolesResponse =
  paths['/api/v1/user-management/roles']['get']['responses']['200']['content']['application/json'];

export type RoleItem = ListRolesResponse['data'][number];

/** Permissions response from GET /api/v1/user-management/permissions */
export interface GetPermissionsResponse {
  permissions: UserManagementPermission[];
}

type listPermisino =paths["/api/v1/user-management/permissions"]['get']['responses']['200']['content']['application/json'];

export const userManagementNestApi = createApi({
  reducerPath: 'userManagementNestApi',
  baseQuery: nestBaseQuery,
  tagTypes: ['UserManagement'],
  keepUnusedDataFor: 3600,
  endpoints: (builder) => ({
    getPermissions: builder.query<listPermisino, void>({
      queryFn: async () => {
        try {
          const result = await client.GET('/api/v1/user-management/permissions');
          return result;
        } catch (err) {
          const e = err as Error & { cause?: string };
          return {
            error: {
              message: e?.message ?? 'An error occurred',
              status: 403,
              cause: e?.cause ?? (e as { error?: string })?.error,
              originalResponse: err,
            },
          };
        }
      },
      providesTags: ['UserManagement'],
    }),
    listInvitations: builder.query<ListInvitationsResponse, ListInvitationsQueryParams | void>({
      query: (params = {}) => {
        const query = {
          page: params?.page ?? 1,
          limit: params?.limit ?? 25,
          searchMode: params?.searchMode ?? 0,
          columnName: (params?.columnName ?? 'invitedTimestamp') as ListInvitationsQueryParams['columnName'],
          orderType: (params?.orderType ?? 'DESC') as 'ASC' | 'DESC',
        };
        return client.GET('/api/v1/user-management/invitations', {
          params: { query },
        });
      },
      providesTags: ['UserManagement'],
    }),
    createInvitation: builder.mutation<CreateInvitationResponse, CreateInvitationRequest>({
      query: (body) =>
        client.POST('/api/v1/user-management/invitations', {
          body,
        }),
      invalidatesTags: ['UserManagement'],
    }),
    listCompanyUsers: builder.query<ListCompanyUsersResponse, void>({
      query: () => client.GET('/api/v1/user-management/companies/users', {}),
      providesTags: ['UserManagement'],
    }),
    removeCompanyUser: builder.mutation< DeleteCompanyUserResponse, string>({
      query: (userId) =>
        client.DELETE('/api/v1/user-management/companies/users/{userId}', {
          params: { path: { userId } },
        }),
      invalidatesTags: ['UserManagement'],
    }),
    lookupUserByEmail: builder.query<LookupUserByEmailResponse, string>({
      query: (email) =>
        client.GET('/api/v1/user-management/users/lookup', {
          params: { query: { email } },
        }),
    }),
    revokeInvitation: builder.mutation<{ id: number; status: string }, number>({
      query: (id) =>
        client.PATCH('/api/v1/user-management/invitations/{id}/revoke', {
          params: { path: { id } },
        }),
      invalidatesTags: ['UserManagement'],
    }),
    listJoinRequests: builder.query<ListJoinRequestsResponse, ListJoinRequestsQueryParams | void>({
      query: (params) =>
        client.GET('/api/v1/user-management/join-requests', {
          params: params?.status ? { query: { status: params.status } } : {},
        }),
      providesTags: ['UserManagement'],
    }),
    rejectInvitation: builder.mutation<{ id: number; status: string }, number>({
      query: (id) =>
        client.PATCH('/api/v1/user-management/invitations/{id}/reject', {
          params: { path: { id } },
        }),
      invalidatesTags: ['UserManagement'],
    }),
    acceptInvitation: builder.mutation<{ id: number; status: string }, number>({
      query: (id) =>
        client.PATCH('/api/v1/user-management/invitations/{id}/accept', {
          params: { path: { id } },
        }),
      invalidatesTags: ['UserManagement'],
    }),
    listRoles: builder.query<ListRolesResponse, void>({
      query: () =>
        client.GET('/api/v1/user-management/roles', {
          params: {},
        }),
      providesTags: ['UserManagement'],
    }),
  }),
});

export const {
  useGetPermissionsQuery,
  useLazyGetPermissionsQuery,
  useListInvitationsQuery,
  useLazyListInvitationsQuery,
  useCreateInvitationMutation,
  useRevokeInvitationMutation,
  useListJoinRequestsQuery,
  useRejectInvitationMutation,
  useAcceptInvitationMutation,
  useListCompanyUsersQuery,
  useLazyListCompanyUsersQuery,
  useRemoveCompanyUserMutation,
  useLazyLookupUserByEmailQuery,
  useListRolesQuery,
} = userManagementNestApi;
